import { useQuery } from '@tanstack/react-query';
import { getKpis, type KpiQueryParams } from '~/apis/analyticsApi';
import {
  getApplicationStatusDistribution,
  getFormCompletionSummary,
  getInspectorPerformance,
  getNewVsRenewals,
  getRegionalNurseryDistribution,
  getYearlyRegistrationTrend,
} from '~/apis/analyticsChartsApi';
import { getApiErrorMessage } from '~/apis/cn02FormApi';

export const analyticsQueryKeys = {
  all: ['analytics'] as const,
  kpis: (dateRangePreset: string, regionalCode: string) =>
    [...analyticsQueryKeys.all, 'kpis', dateRangePreset, regionalCode] as const,
  applicationStatus: (regionalCode: string, year: number) =>
    [...analyticsQueryKeys.all, 'application-status', regionalCode, year] as const,
  yearlyTrend: () => [...analyticsQueryKeys.all, 'yearly-trend'] as const,
  regionalNursery: () => [...analyticsQueryKeys.all, 'regional-nursery'] as const,
  newVsRenewals: (regionalCode: string, year: number | undefined) =>
    [...analyticsQueryKeys.all, 'new-vs-renewals', regionalCode, year ?? 'all'] as const,
  formCompletion: (regionalCode: string, year: number) =>
    [...analyticsQueryKeys.all, 'form-completion', regionalCode, year] as const,
  inspectorPerformance: (regionalCode: string) =>
    [...analyticsQueryKeys.all, 'inspector-performance', regionalCode] as const,
};

function toErrorMessage(error: unknown, fallback: string): string | null {
  return error ? getApiErrorMessage(error, fallback) : null;
}

export function useKpisQuery(params: KpiQueryParams, options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: analyticsQueryKeys.kpis(params.dateRangePreset, params.regionalCode),
    queryFn: () => getKpis(params),
    enabled: options?.enabled ?? true,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    errorMessage: toErrorMessage(query.error, 'Could not load key performance indicators.'),
  };
}

export function useApplicationStatusDistributionQuery(regionalCode: string, year: number) {
  const query = useQuery({
    queryKey: analyticsQueryKeys.applicationStatus(regionalCode, year),
    queryFn: () => getApplicationStatusDistribution({ regionalCode, year }),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    errorMessage: toErrorMessage(query.error, 'Could not load application status distribution.'),
  };
}

export function useYearlyRegistrationTrendQuery() {
  const query = useQuery({
    queryKey: analyticsQueryKeys.yearlyTrend(),
    queryFn: getYearlyRegistrationTrend,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    errorMessage: toErrorMessage(query.error, 'Could not load yearly registration trend.'),
  };
}

export function useRegionalNurseryDistributionQuery() {
  const query = useQuery({
    queryKey: analyticsQueryKeys.regionalNursery(),
    queryFn: getRegionalNurseryDistribution,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    errorMessage: toErrorMessage(query.error, 'Could not load nursery distribution.'),
  };
}

export function useNewVsRenewalsQuery(regionalCode: string, year: number | undefined) {
  const query = useQuery({
    queryKey: analyticsQueryKeys.newVsRenewals(regionalCode, year),
    queryFn: () => getNewVsRenewals(year ? { regionalCode, year } : { regionalCode }),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    errorMessage: toErrorMessage(query.error, 'Could not load new vs renewal applications.'),
  };
}

export function useFormCompletionSummaryQuery(regionalCode: string, year: number) {
  const query = useQuery({
    queryKey: analyticsQueryKeys.formCompletion(regionalCode, year),
    queryFn: () => getFormCompletionSummary({ regionalCode, year }),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    errorMessage: toErrorMessage(query.error, 'Could not load form completion summary.'),
  };
}

export function useInspectorPerformanceQuery(regionalCode: string) {
  const query = useQuery({
    queryKey: analyticsQueryKeys.inspectorPerformance(regionalCode),
    queryFn: () => getInspectorPerformance({ regionalCode }),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    errorMessage: toErrorMessage(query.error, 'Could not load inspector performance.'),
  };
}
